import {
	PaletteBand,
	PaletteDefinition,
	PaletteDurabilityComfortBias,
	PaletteInputs,
	PaletteIntendedUse,
	PaletteMaterialCharacter,
	PaletteScoreBreakdown,
	PaletteWellnessIntent,
	PaletteDaylightViewsAssumption,
	PaletteAcousticIntent,
} from '@/types/palette';

const WELLNESS_INTENT_BANDS: Record<PaletteWellnessIntent, PaletteBand> = {
	code_minimum_baseline: { min: 3.0, max: 4.5 },
	above_baseline: { min: 5.0, max: 6.5 },
	wellness_focused: { min: 6.5, max: 7.5 },
	biophilic_regenerative: { min: 7.5, max: 9.0 },
};

const INTENDED_USE_ADJUSTMENTS: Record<PaletteIntendedUse, number> = {
	office_commercial: 0.0,
	healthcare: -0.2,
	k12_education: -0.1,
	higher_education: 0.0,
	residential: 0.2,
	civic_community: 0.2,
	laboratory_industrial: -0.4,
	other: 0.0,
};

const MATERIAL_CHARACTER_ADJUSTMENTS: Record<PaletteMaterialCharacter, number> = {
	predominantly_synthetic: -1.0,
	mixed_synthetic_natural: 0.0,
	predominantly_natural: 1.0,
	exposed_structure_minimal_finishes: 0.0,
};

const DAYLIGHT_VIEWS_ADJUSTMENTS: Record<PaletteDaylightViewsAssumption, number> = {
	limited_daylight_controlled: -1.0,
	moderate_daylight_access: 0.0,
	high_daylight_with_glare_control: 1.0,
	daylight_intentional_views_nature: 1.0,
};

//@note: Check with the client to see if this is correct (0.0).
const ACOUSTIC_INTENT_ADJUSTMENTS: Record<PaletteAcousticIntent, number> = {
	performance_driven_noise_tolerant: 0.0,
	balanced: 0.0,
	acoustically_calm_restorative: 0.0,
};

//@note: Check with the client to see if this is correct (0.0).
const DURABILITY_COMFORT_ADJUSTMENTS: Record<PaletteDurabilityComfortBias, number> = {
	durability_first: 0.0,
	balanced: 0.0,
	comfort_experience_first: 0.0,
};

const MAX_SINGLE_ADJUSTMENT = 1;

function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

function round(value: number): number {
	return Number(value.toFixed(2));
}

function clampAdjustment(adjustment: number): number {
	return clamp(adjustment, -MAX_SINGLE_ADJUSTMENT, MAX_SINGLE_ADJUSTMENT);
}

export function getIntentBand(wellnessIntent: PaletteWellnessIntent): PaletteBand {
	return WELLNESS_INTENT_BANDS[wellnessIntent];
}

export function computePaletteBaselineScore(inputs: PaletteInputs): PaletteScoreBreakdown {
	const band = getIntentBand(inputs.wellnessIntent);
	const bandMidpoint = (band.min + band.max) / 2;

	const appliedAdjustments = {
		intendedUse: clampAdjustment(INTENDED_USE_ADJUSTMENTS[inputs.intendedUse]),
		materialCharacter: clampAdjustment(
			MATERIAL_CHARACTER_ADJUSTMENTS[inputs.materialCharacter]
		),
		daylightViewsAssumption: clampAdjustment(
			DAYLIGHT_VIEWS_ADJUSTMENTS[inputs.daylightViewsAssumption]
		),
		acousticIntent: clampAdjustment(ACOUSTIC_INTENT_ADJUSTMENTS[inputs.acousticIntent]),
		durabilityComfortBias: clampAdjustment(
			DURABILITY_COMFORT_ADJUSTMENTS[inputs.durabilityComfortBias]
		),
	};

	const adjustmentTotal = Object.values(appliedAdjustments).reduce(
		(sum, value) => sum + value,
		0
	);
	const rawScore = bandMidpoint + adjustmentTotal;
	const clampedScore = clamp(rawScore, band.min, band.max);

	const explanation = `Band ${band.min}-${band.max} from wellness intent; adjustments ${round(adjustmentTotal)} from use, material character, daylight/views, acoustic intent, and durability/comfort bias.`;

	return {
		band,
		bandMidpoint: round(bandMidpoint),
		appliedAdjustments,
		adjustmentTotal: round(adjustmentTotal),
		rawScore: round(rawScore),
		clampedScore: round(clampedScore),
		explanation,
	};
}

export function createPaletteDefinition(
	input: Pick<PaletteDefinition, 'id' | 'name' | 'inputs' | 'materialAssumptions'>,
	nowIso: string = new Date().toISOString()
): PaletteDefinition {
	const score = computePaletteBaselineScore(input.inputs);

	return {
		id: input.id,
		name: input.name,
		inputs: input.inputs,
		baselineScore: score.clampedScore,
		band: score.band,
		explanation: score.explanation,
		materialAssumptions: input.materialAssumptions,
		createdAt: nowIso,
		updatedAt: nowIso,
	};
}
